/**
 * Checkout page - Stripe payment processing
 */

import { useState, useMemo, useEffect } from 'react';
import { useCart } from '@/contexts/CartContext';
import { useCompany } from '@/contexts/CompanyContext';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useStripeCheckout } from '@/hooks/useStripeCheckout';
import { ShoppingCartIcon } from '@heroicons/react/24/outline';
import { env } from '@/config/env';
import type { TransactionModel, TransactionDetail } from '@/types';

export function CheckoutPage() {
  const { items, getTotalAmount, clearCart } = useCart();
  const { companyIdNumber } = useCompany();
  const { isAuthenticated, user, donor } = useAuth();
  const navigate = useNavigate();
  const { loading: stripeLoading, error: stripeError, createSession } = useStripeCheckout();

  const [addProcessingFees, setAddProcessingFees] = useState(true);
  const [isRecurring, setIsRecurring] = useState(false);
  // 1 = Monthly (default), 2 = Yearly, 3 = Daily
  const [recurringFrequency, setRecurringFrequency] = useState(1);
  const [recurringCount, setRecurringCount] = useState(12);
  const [isDonateAnonymously, setIsDonateAnonymously] = useState(false);
  const [donorEmail, setDonorEmail] = useState('');
  const [donorName, setDonorName] = useState('');
  const [emailError, setEmailError] = useState('');
  const [nameError, setNameError] = useState('');

  // Pre-populate donor fields when the user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // Prefer donor DB record, fall back to Auth0 user profile
      const email = donor?.eMail || user?.email || '';
      const name =
        donor?.completeName ||
        (donor?.firstName && donor?.lastName
          ? `${donor.firstName} ${donor.lastName}`.trim()
          : undefined) ||
        user?.name ||
        '';
      setDonorEmail(email);
      setDonorName(name);
    }
  }, [isAuthenticated, donor, user]);

  // Scroll to top when checkout page loads
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const totalAmount = getTotalAmount();

  // Get purposeId from first cart item (all items should have same purpose)
  const purposeId = items && items.length > 0 && items[0] ? items[0].purposeId : 1;
  const isServicePayment = purposeId === 2;

  // Calculate processing fees using env configuration
  const processingFees = useMemo(() => {
    if (!addProcessingFees) return 0;
    return Math.round(totalAmount * env.onlineFeePercentage + env.onlineFeeFixedAmountCents);
  }, [totalAmount, addProcessingFees]);

  const finalTotal = totalAmount + processingFees;
  const formattedTotal = (totalAmount / 100).toFixed(2);
  const formattedFees = (processingFees / 100).toFixed(2);
  const formattedFinalTotal = (finalTotal / 100).toFixed(2);

  const handleCheckout = async () => {
    // Validate email
    if (!donorEmail.trim()) {
      setEmailError('Email is required for payment processing');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(donorEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setEmailError('');

    // Validate name (required unless anonymous mode is enabled and checked)
    if (!env.onlineEnableAnonymous || !isDonateAnonymously) {
      if (!donorName.trim()) {
        setNameError('Full name is required');
        return;
      }
      setNameError('');
    }

    if (!companyIdNumber) {
      alert('Company ID is required');
      return;
    }

    if (items.length === 0) {
      alert('Your cart is empty');
      return;
    }

    try {
      // Build transaction details from cart items (without fees per item)
      const details: TransactionDetail[] = items.map((item, index) => ({
        lineNumber: index + 1,
        description: isServicePayment
          ? item.serviceType?.detailedTextHeader || item.serviceType?.description || 'Service'
          : item.donationType?.detailedTextHeader || item.donationType?.description || 'Donation',
        donationTypeId: isServicePayment
          ? item.serviceType?.donationType || 0
          : item.donationType?.donationType || 0,
        amount: item.amount / 100,
      }));

      // Add processing fees as a separate line item if enabled
      if (addProcessingFees && processingFees > 0) {
        details.push({
          lineNumber: details.length + 1,
          description: env.onlineFeeDonationTypeDescription,
          donationTypeId: env.onlineFeeDonationTypeId,
          amount: processingFees / 100,
        });
      }

      const request: TransactionModel = {
        transactionId: 0, // 0 for new transactions
        companyId: companyIdNumber,
        donorId: env.onlineEnableAnonymous && isDonateAnonymously ? env.anonymousDonorId : 0,
        donorName:
          env.onlineEnableAnonymous && isDonateAnonymously ? 'Anonymous' : donorName.trim(),
        donorEmail: donorEmail.trim(),
        donorPhone: undefined,
        transactionDate: new Date().toISOString(),
        purposeId: purposeId, // Use purposeId from cart items (1 for donation, 2 for service)
        totalAmount: finalTotal / 100, // Include processing fees if selected
        currency: env.onlineCurrency.toUpperCase(),
        recurring: isRecurring,
        numberofMonths: isRecurring ? recurringCount : undefined,
        recurringFrequency: isRecurring ? recurringFrequency : undefined,
        successUrl: `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/cancel`,
        details: details,
      };

      await createSession(request);
      // User will be redirected to Stripe Checkout
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Failed to start checkout. Please try again.');
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-2/3 text-center space-y-6">
          <ShoppingCartIcon className="h-16 w-16 mx-auto text-gray-400" />
          <h2 className="text-2xl font-semibold text-gray-800">Your cart is empty</h2>
          <p className="text-gray-600">
            Add some {purposeId === 2 ? 'services' : 'donations'} to your cart before proceeding to
            checkout.
          </p>
          <button
            onClick={() => navigate(purposeId === 2 ? '/services' : '/donate')}
            className="px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors"
          >
            {purposeId === 2 ? 'Continue Shopping' : 'Continue Donating'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Checkout</h1>
        <p className="mt-2 text-gray-600">Review your donations and complete payment</p>
      </div>

      {/* Mobile: Donor info first, then cart, then summary */}
      {/* Desktop: Cart on left (2/3), Summary on right (1/3) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column on desktop, full width on mobile */}
        <div className="lg:col-span-2 space-y-4 order-1 lg:order-1">
          {/* Donor Information Form - shows first on mobile */}
          <div className="bg-white rounded-xl shadow-lg p-6 order-1 lg:order-2">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {isServicePayment ? 'Customer Information' : 'Donor Information'}
            </h2>
            {isAuthenticated && user && (
              <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                {user.picture && (
                  <img
                    src={user.picture}
                    alt={user.name || 'User'}
                    className="h-6 w-6 rounded-full object-cover"
                  />
                )}
                <span>
                  Signed in as <strong>{user.name || user.email}</strong> — your information has
                  been pre-filled.
                </span>
              </div>
            )}
            <div className="space-y-4">
              {/* Email (Required) */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  value={donorEmail}
                  onChange={(e) => {
                    setDonorEmail(e.target.value);
                    setEmailError('');
                  }}
                  disabled={isDonateAnonymously}
                  placeholder="your.email@example.com"
                  className={`w-full px-4 py-3 border rounded-lg text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                    emailError ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {emailError && <p className="mt-1 text-sm text-red-600">{emailError}</p>}
                <p className="mt-1 text-sm text-gray-500">Receipt will be sent to this email</p>
              </div>

              {/* Name (Required unless anonymous mode enabled and checked) */}
              {(!env.onlineEnableAnonymous || !isDonateAnonymously) && (
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={donorName}
                    onChange={(e) => {
                      setDonorName(e.target.value);
                      setNameError('');
                    }}
                    placeholder="John Doe"
                    className={`w-full px-4 py-3 border rounded-lg text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      nameError ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                  />
                  {nameError && <p className="mt-1 text-sm text-red-600">{nameError}</p>}
                </div>
              )}

              {stripeError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800 text-sm">{stripeError.message}</p>
                </div>
              )}
            </div>
          </div>

          {/* Order Summary - shows after donor info on mobile */}
          <div className="bg-white rounded-xl shadow-lg p-6 order-2 lg:order-1">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Summary</h2>

            <div className="space-y-4">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-start justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">
                      {isServicePayment
                        ? item.serviceType?.detailedTextHeader ||
                          `Service ${item.serviceType?.description}`
                        : item.donationType?.detailedTextHeader ||
                          `Donation ${item.donationType?.description}`}
                    </div>
                    {item.donationType && isServicePayment && (
                      <div className="text-sm text-gray-600 mt-1">
                        Donation:{' '}
                        {item.donationType.detailedTextHeader || item.donationType.description}
                      </div>
                    )}
                  </div>
                  <div className="text-xl font-bold text-primary-600 ml-4">
                    ${(item.amount / 100).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Total and Actions - Right column on desktop, bottom on mobile */}
        <div className="lg:col-span-1 order-3 lg:order-2">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden lg:sticky lg:top-20">
            {/* Total Header */}
            <div className="bg-primary-600 px-6 py-4">
              <h3 className="text-2xl font-bold text-white text-center">
                {isServicePayment
                  ? `Your total Service Fee is $${formattedFinalTotal}`
                  : `Your total Donation is $${formattedFinalTotal}`}
              </h3>
            </div>

            {/* Options */}
            <div className="p-6 space-y-4">
              {/* Processing Fees Checkbox */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={addProcessingFees}
                  onChange={(e) => setAddProcessingFees(e.target.checked)}
                  className="mt-1 h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                />
                <span className="flex-1 text-sm text-gray-700 group-hover:text-gray-900">
                  {env.onlineFeeMessage}
                </span>
              </label>

              {/* Recurring Payment Checkbox */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={isRecurring}
                  onChange={(e) => {
                    setIsRecurring(e.target.checked);
                    if (!e.target.checked) {
                      setRecurringFrequency(1);
                      setRecurringCount(12);
                    }
                  }}
                  className="mt-1 h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                />
                <span className="flex-1 text-sm text-gray-700 group-hover:text-gray-900">
                  Make this a Recurring Charge
                </span>
              </label>

              {/* Recurring options (shown when recurring is selected) */}
              {isRecurring && (
                <div className="ml-0 space-y-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  {/* Frequency selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Frequency
                    </label>
                    <div className="flex gap-2">
                      {[
                        { label: 'Daily', value: 3 },
                        { label: 'Monthly', value: 1 },
                        { label: 'Yearly', value: 2 },
                      ].map(({ label, value }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => {
                            setRecurringFrequency(value);
                            setRecurringCount(value === 3 ? 30 : value === 2 ? 1 : 12);
                          }}
                          className={`flex-1 py-1.5 px-2 text-sm font-medium rounded-md border transition-colors ${
                            recurringFrequency === value
                              ? 'bg-primary-600 text-white border-primary-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Count selector based on frequency */}
                  <div>
                    <label
                      htmlFor="recurringCount"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      {recurringFrequency === 3
                        ? 'Number of Days'
                        : recurringFrequency === 2
                          ? 'Number of Years'
                          : 'Number of Months'}
                    </label>
                    <select
                      id="recurringCount"
                      value={recurringCount}
                      onChange={(e) => setRecurringCount(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {recurringFrequency === 3 && (
                        <>
                          <option value={5}>5 Days</option>
                          <option value={10}>10 Days</option>
                          <option value={15}>15 Days</option>
                          <option value={20}>20 Days</option>
                          <option value={25}>25 Days</option>
                          <option value={30}>30 Days</option>
                        </>
                      )}
                      {recurringFrequency === 1 && (
                        <>
                          <option value={1}>1 Month</option>
                          <option value={3}>3 Months</option>
                          <option value={6}>6 Months</option>
                          <option value={9}>9 Months</option>
                          <option value={12}>12 Months</option>
                          <option value={15}>15 Months</option>
                          <option value={18}>18 Months</option>
                          <option value={24}>24 Months</option>
                          <option value={36}>36 Months</option>
                        </>
                      )}
                      {recurringFrequency === 2 && (
                        <>
                          <option value={1}>1 Year</option>
                          <option value={2}>2 Years</option>
                          <option value={3}>3 Years</option>
                          <option value={4}>4 Years</option>
                          <option value={5}>5 Years</option>
                        </>
                      )}
                    </select>
                  </div>
                </div>
              )}

              {/* Donate Anonymously Checkbox */}
              {!isServicePayment && env.onlineEnableAnonymous && (
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={isDonateAnonymously}
                    onChange={(e) => setIsDonateAnonymously(e.target.checked)}
                    className="mt-1 h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                  />
                  <span className="flex-1 text-sm text-gray-700 group-hover:text-gray-900">
                    Donate Anonymously
                  </span>
                </label>
              )}
            </div>

            {/* Total Breakdown */}
            <div className="px-6 pb-6">
              <div className="space-y-2 mb-6 border-t pt-4">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal:</span>
                  <span>${formattedTotal}</span>
                </div>
                {addProcessingFees && (
                  <div className="flex justify-between text-gray-600">
                    <span>Processing Fees:</span>
                    <span>${formattedFees}</span>
                  </div>
                )}
                <div className="border-t pt-2 flex justify-between text-xl font-bold text-gray-900">
                  <span>Total:</span>
                  <span className="text-primary-600">${formattedFinalTotal}</span>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleCheckout}
                  disabled={stripeLoading || !donorEmail.trim()}
                  className="w-full py-3 px-4 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {stripeLoading ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    'Complete Payment'
                  )}
                </button>

                <button
                  onClick={() => navigate(isServicePayment ? '/services' : '/donate')}
                  className="w-full py-3 px-4 border-2 border-primary-600 text-primary-600 font-semibold rounded-lg hover:bg-primary-50 transition-colors"
                >
                  {isServicePayment ? 'Continue Shopping' : 'Continue Donating'}
                </button>

                <button
                  onClick={() => {
                    if (confirm('Are you sure you want to clear your cart?')) {
                      clearCart();
                      navigate('/donate');
                    }
                  }}
                  className="w-full py-2 px-4 text-gray-500 hover:text-red-600 text-sm transition-colors"
                >
                  Clear Cart
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
